/**
 * iOS 加主屏引导 banner
 * - iOS Safari: navigator.standalone 检测是否在 PWA 模式
 * - 通用: window.matchMedia('(display-mode: standalone)')
 * - 仅 iOS Safari 显示 (Android Chrome 走原生安装提示)
 * - 用户关闭后 localStorage 记 7 天不再显示
 */
import { useEffect, useState } from 'react';
import { haptic } from '../hooks/useHaptics';

const DISMISS_KEY = 'todo_a2hs_dismissed';
const DISMISS_DAYS = 7;

function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !('MSStream' in window);
}

function isInStandalone(): boolean {
  // iOS Safari
  if ((navigator as Navigator & { standalone?: boolean }).standalone) return true;
  // 通用 (Android Chrome / iOS PWA / Desktop PWA)
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  return false;
}

function recentlyDismissed(): boolean {
  const ts = localStorage.getItem(DISMISS_KEY);
  if (!ts) return false;
  const age = Date.now() - parseInt(ts, 10);
  return age < DISMISS_DAYS * 24 * 60 * 60 * 1000;
}

export function AddToHomeBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // 只在 iOS Safari + 不在 PWA + 没被关闭过时显示
    if (!isIOS()) return;
    if (isInStandalone()) return;
    if (recentlyDismissed()) return;
    // 首次访问延迟 5s 出现, 避免一进 app 就打扰
    const timer = setTimeout(() => setShow(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  if (!show) return null;

  const handleDismiss = () => {
    haptic('light');
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-30 flex justify-center px-3 safe-bottom sm:bottom-6">
      <div className="pointer-events-auto flex w-full max-w-md items-start gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl dark:border-gray-700 dark:bg-gray-800">
        <div className="shrink-0 rounded-xl bg-blue-500 p-2">
          <svg
            className="h-6 w-6 text-white"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 5v14M5 12l7 7 7-7"
            />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium">把 Todo 加到主屏幕</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            像原生 app 一样使用, 支持后台推送提醒。
          </div>
          <ol className="mt-2 space-y-0.5 text-xs text-gray-600 dark:text-gray-300">
            <li>
              <span className="text-blue-500 font-medium">1.</span> 点底部分享{' '}
              <span className="inline-block rounded bg-gray-100 px-1.5 py-0.5 dark:bg-gray-700">
                <svg
                  className="inline h-3 w-3"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path d="M10 3a1 1 0 011 1v9.59l2.7-2.7a1 1 0 111.4 1.42l-4.3 4.3a1 1 0 01-1.4 0l-4.3-4.3a1 1 0 011.4-1.42L9 13.59V4a1 1 0 011-1zM4 17a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1z" />
                </svg>
              </span>{' '}
              按钮
            </li>
            <li>
              <span className="text-blue-500 font-medium">2.</span> 选择"添加到主屏幕"
            </li>
            <li>
              <span className="text-blue-500 font-medium">3.</span> 点"添加"完成
            </li>
          </ol>
        </div>
        <button
          onClick={handleDismiss}
          className="shrink-0 rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          aria-label="关闭提示"
        >
          <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
